import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  ListSubheader,
  InputAdornment
} from '@mui/material';
import { isDecimal } from "validator";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { UpdateProject } from 'src/models/project_details';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  getProjectBilling,
  getProjectNature,
  getProjectStatus,
  getSingleProject,
  UpdateProjectDetail,
  getSalesStage
} from 'src/services/projectDetailService';
import {
  getExternalLeads,
  getSegmentByCustomer,
  getStakeholdersById
} from 'src/services/projectService';
import moment from 'moment';
import ConfirmationModal from '../common/confirmationModal/ConfirmationModal';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { getRolePermissions } from 'src/services/authService';
import { PieChart } from 'react-minimal-pie-chart';
import { getAllEmployees, getJiraLeadEmployees } from 'src/services/employeeService';
import { changeStringLength } from 'src/utils/stringFormat';
import { getActualHours } from 'src/services/timesheetService';
import { useAppSelector } from 'src/hooks/hooks';
import { AppActions } from 'src/store/AppReducer';
import SearchIcon from '@mui/icons-material/Search';
import { getStakeholderRole } from '../../services/projectDetailService';
import { DatePicker } from '@mui/lab';
import 'src/content/projectDetails/ProjectDetails.css';
import { validateMultiRolesAndPermissions } from 'src/utils/validate';

interface EmployeeEditProps {}

const useQuery = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

const ProjectEditForm: FC<EmployeeEditProps> = () => {
  const [isStakeholderName, isSetStakeholderName] = useState(false);
  const [isStakeholderRole, isSetStakeholderRole] = useState(false);

  const navigate = useNavigate();
  const { projectCode } = useParams();
  let query = useQuery();
  const [customerId, setcustomerId] = useState('');
  const [stakeholders, setStakeholders] = useState<any>([]);
  const [segmentData, setSegmentData] = useState<any>({});
  const [visibility, setVisibility] = useState('');
  const [stakeholderRoles, setStakeholderRoles] = useState<any>([]);

  useEffect(() => {
    setcustomerId(query.get('client'));
    setVisibility(query.get('view'));
  }, [query]);

  useEffect(() => {
    if(customerId) {
      getStakeholdersById(customerId).then((res) => {
        setStakeholders(res.data);
      });
      getSegmentByCustomer(customerId).then((res) => {
        setSegmentData(res.data);
      });
    }
  }, [customerId]);

  const getStakeholderRoleData = useCallback(() => {
    getStakeholderRole().then((res) => {
       setStakeholderRoles(res.data);
      });
  }, []);

  useEffect(() => {
    getStakeholderRoleData();
  }, []);

  const [projectDetails, setProjectDetails] = useState<UpdateProject>({
    actualEffectStatus: '',
    actualRemainingEffort: '',
    billingCategory: '',
    id: '',
    noteForInvoice: '',
    isClosed: true,
    key: '',
    name: '',
    nature: '',
    plannedHours: '',
    poStatus: '',
    projectCompletion: '',
    remainingEffort: '',
    sowStatus: '',
    projectStatus: '',
    type: '',
    preSalesStartDate: '',
    preSalesEndDate: '',
    actualStartDate: '',
    plannedEndDate: '',
    plannedStartDate: '',
    actualEndDate: '',
    projectStakeHolders: [],
    revisedPlannedHours: '',
    //leadType: '',
    //leadName: '',
    //leadId: '',
    customer: '',
    projectLeadId: '',
    salesStage: ''
  });

  const [validationErrors, setValidationErrors] = useState({
    actualEffectStatusError: '',
    actualRemainingEffortError: '',
    billingCategoryError: '',
    invoiceNoteError: '',
    isClosedError: '',
    keyError: '',
    nameError: '',
    natureError: '',
    plannedHoursError: '',
    poStatusError: '',
    projectCompletionError: '',
    remainingEffortError: '',
    sowStatusError: '',
    projectStatusError: '',
    typeError: '',
    preSalesStartDateError: '',
    preSalesEndDateError: '',
    actualStartDateError: '',
    plannedEndDateError: '',
    plannedStartDateError: '',
    actualEndDateError: '',
    projectStakeHoldersError: '',
    revisedPlannedHoursError: '',
    //leadTypeError: '',
    //leadNameError: '',
    leadIdError: '',
    customerError: '',
    projectLeadError: '',
    stakeholderDuplicateError: '',
    preSaleStageError: ''
  });

  const dispatch = useDispatch();

  const [projectNatures, setPojectNatures] = useState([]);
  const [billingCategories, setbillingCategories] = useState([]);
  const [projectStatus, setprojectStatus] = useState([]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [externalLeads, setExternalLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedStakeholder, setSelectedStakeholder] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStakeholders, setSelectedStakeholders] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [fields, setFields] = useState([]);
  const [jiraLeadEmployees, setJiraLeadEmployees] = useState([]);
  const [saleStages, setSaleStages] = useState([]);

  useEffect(() => {
    if (
      projectDetails?.projectStakeHolders?.length > 0 &&
      selectedStakeholders.length === 0
    ) {
      const stakes = projectDetails?.projectStakeHolders?.map((pro) => {
        return {
          stakeholderId: pro?.stakeholder?.id,
          stakeholderName: pro?.stakeholder?.name,
          designation: pro?.designation,
          stakeholderRole: pro?.stakeholderRole?.value,
        };
      });
      setSelectedStakeholders([...stakes]);
    }
  }, [projectDetails]);

  const toggleConfirm = () => {
    setValidationErrors((prev) => ({
      ...prev,
      stakeholderDuplicateError: ''
    }));
    if(!projectDetails.plannedHours) {
      setValidationErrors((prev) => ({
        ...prev,
        plannedHoursError: 'Please enter the planned hours'
      }));
    }else if(projectDetails.type === 'Pre-sale') {
      if(!projectDetails.preSalesStartDate || projectDetails.preSalesStartDate === '0001-01-01T00:00:00') {
        setValidationErrors((prev) => ({
          ...prev,
          preSalesStartDateError: 'Please select the pre-sale start date'
        }));
      }else if(!projectDetails.preSalesEndDate || projectDetails.preSalesEndDate === '0001-01-01T00:00:00') {
        setValidationErrors((prev) => ({
          ...prev,
          preSalesEndDateError: 'Please select the pre-sale end date'
        }));
      }else if(!projectDetails.salesStage) {
        setValidationErrors((prev) => ({
          ...prev,
          preSaleStageError: 'Please select the pre-sale stage'
        }));
      }else {
        setOpenConfirmationModal(!openConfirmationModal);
      }
    }else if(projectDetails.type === 'Delivery') {
      if(!projectDetails.plannedStartDate || projectDetails.plannedStartDate === '0001-01-01T00:00:00') {
        setValidationErrors((prev) => ({
          ...prev,
          plannedStartDateError: 'Please select the planned start date'
        }));
      }else if(!projectDetails.plannedEndDate || projectDetails.plannedEndDate === '0001-01-01T00:00:00') {
        setValidationErrors((prev) => ({
          ...prev,
          plannedEndDateError: 'Please select the planned end date'
        }));
      }
      else {
        setOpenConfirmationModal(!openConfirmationModal);
      }
    }
    else {
      setOpenConfirmationModal(!openConfirmationModal);
    }
  };

  const getAllProjectDropdowns = () => {
    getProjectNature().then((res) => {
      setPojectNatures(res.data);
    });
    getProjectBilling().then((res) => {
      setbillingCategories(res.data);
    });
    getProjectStatus().then((res) => {
      setprojectStatus(res.data);
    });
    getExternalLeads().then((res) => {
      setExternalLeads(res.data);
    });
    getAllEmployees().then((res) => {
      setEmployees(res.data);
    });
    getJiraLeadEmployees().then((res) => {
      setJiraLeadEmployees(res.data);
    });
    getSalesStage().then((res) => {
      setSaleStages(res.data);
    });
  };

  useEffect(() => {
    getAllProjectDropdowns();
  }, []);

  const getSingleProjectData = useCallback(() => {
    dispatch(AppActions.showLoader());
    getSingleProject(projectCode).then((res) => {
      const data = res.data;
      data.projectStatus = data.status;
      setProjectDetails(data as UpdateProject);
      dispatch(AppActions.hideLoader());
    });
  }, [dispatch, projectCode]);

  const getPermissions = (mail) => {
    getRolePermissions(mail).then((res) => {
      const tabs = validateMultiRolesAndPermissions(res);
      const arr = tabs.filter((fil) => fil.name === 'Project');
      setPermissions(arr[0].permissions);
      setFields(arr[0].fields);
    });
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (Object.keys(user).length === 0) {
      navigate('/');
    } else {
      getPermissions(user.Email);
    }
  }, [navigate]);

  useEffect(() => {
    getSingleProjectData();
  }, [getSingleProjectData, projectCode]);

  // removed get values
  const { register, handleSubmit, reset, setValue } = useForm<UpdateProject>();

  const { isLoading } = useAppSelector((state) => state.appReducer);

  const onSubmit = () => {
    // setValidationErrors(errorsObj);
    const isEmpty = Object.values(validationErrors).every(
      (x) => x === null || x === ''
    );

    if (isEmpty) {
      if (
        (projectDetails.name === '' || projectDetails.name === null) &&
        fields.some((field) => field.name === 'Name' && !field.edit)
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          projectNameErr: 'Please enter the project name'
        }));
      } else if (
        (projectDetails.key === '' || projectDetails.key === null) &&
        fields.some((field) => field.name === 'Project Code' && !field.edit)
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          projectCodeErr: 'Please enter the project code'
        }));
      } else if (
        (projectDetails.nature === '' || projectDetails.nature === null) &&
        fields.some((field) => field.name === 'Project Nature' && !field.edit)
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          projectNatureError: 'Please select the project nature'
        }));
      } else if (
        (projectDetails.type === '' || projectDetails.type === null) &&
        fields.some((field) => field.name === 'Project Type' && !field.edit)
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          projectTypeError: 'Please select the project type'
        }));
      } else if (
        (projectDetails.projectStatus === '' ||
          projectDetails.projectStatus === null) &&
        fields.some((field) => field.name === 'Project Status' && !field.edit)
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          projectStatusError: 'Please select the project status'
        }));
      } else if (projectDetails.projectStakeHolders.length === 0) {
        setValidationErrors((prev) => ({
          ...prev,
          projectStakeHoldersError: 'Project Stakeholders Required'
        }));
      } else if (
        (projectDetails.billingCategory === '' ||
          projectDetails.billingCategory === null) &&
        fields.some(
          (field) => field.name === 'Project Billing Category' && !field.edit
        )
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          billingCategoryError: 'Please select the billing category'
        }));
      } else if (
        (projectDetails.sowStatus === '' ||
          projectDetails.sowStatus === null) &&
        fields.some((field) => field.name === 'SOW Status' && !field.edit)
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          sowStatusError: 'Please select the sow / po statusy'
        }));
      } else if (
        (projectDetails.poStatus === '' || projectDetails.poStatus === null) &&
        fields.some((field) => field.name === 'PO Status' && !field.edit)
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          poStatusError: 'Please select the po status'
        }));
      } else if (
        (projectDetails.plannedHours === '' ||
          projectDetails.plannedHours === null) &&
        fields.some((field) => field.name === 'Planned Hours' && !field.edit)
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          plannedHoursError: 'Please enter the planned hours'
        }));
      } else if (
        (projectDetails.preSalesStartDate === '' ||
          projectDetails.preSalesStartDate === null) &&
        fields.some(
          (field) => field.name === 'Pre-sale Start Date' && !field.edit
        )
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          preSalesStartDateError: 'Please select the pre-sale start date'
        }));
      } else if (
        (projectDetails.plannedEndDate === '' ||
          projectDetails.plannedEndDate === null) &&
        fields.some((field) => field.name === 'Planned End Date' && !field.edit)
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          plannedEndDateError: 'Please select the planned end date'
        }));
      } else if (
        Date.parse(projectDetails.preSalesStartDate) >
        Date.parse(projectDetails.preSalesEndDate)
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          preSalesEndDateError:
            'Pre Sale End date should be greater than start date'
        }));
      } else if (
        Date.parse(projectDetails.plannedStartDate) >
        Date.parse(projectDetails.plannedEndDate)
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          plannedEndDateError:
            'Planned End date should be greater than start date'
        }));
      } else {
        dispatch(AppActions.showLoader());
        projectDetails.plannedEndDate = moment(
          projectDetails.plannedEndDate
        ).format('MM/DD/YYYY');
        projectDetails.preSalesStartDate = moment(
          projectDetails.preSalesStartDate
        ).format('MM/DD/YYYY');
        projectDetails.preSalesEndDate = moment(
          projectDetails.preSalesEndDate
        ).format('MM/DD/YYYY');
        projectDetails.plannedStartDate = moment(
          projectDetails.plannedStartDate
        ).format('MM/DD/YYYY');
        projectDetails.actualStartDate = projectDetails.actualStartDate
          ? moment(projectDetails.actualStartDate).format('MM/DD/YYYY')
          : null;
        projectDetails.actualEndDate = projectDetails.actualEndDate
          ? moment(projectDetails.actualEndDate).format('MM/DD/YYYY')
          : null;

        const stakeholdersArr = projectDetails?.projectStakeHolders?.map(
          (stake) => {
            if (stake.designation) {
              return {
                designation: stake?.designation,
                stakeholderId: stake?.stakeholder?.id || stake.stakeholderId
              };
            } else {
              return {
                stakeholderId: stake?.stakeholder?.id || stake?.stakeholderId,
                stakeholderRoleId:
                  stake?.stakeholderRole?.id || stake?.stakeholderRoleId
              };
            }
          }
        );

        const body = {
          id: projectDetails?.id,
          projectStakeHolders: [...stakeholdersArr],
          customerId: projectDetails?.customer?.id,
          type: projectDetails.type,
          key: projectDetails.key,
          name: projectDetails.name,
          nature: projectDetails.nature,
          //leadResourceType: projectDetails.leadType,
          //leadResourceId: projectDetails.leadId,
          //leadResourceName: projectDetails.leadName,
          projectLeadId: projectDetails.projectLeadId,
          projectStatus: projectDetails.projectStatus,
          salesStage: projectDetails.salesStage,
          billingCategory: projectDetails.billingCategory,
          sowStatus: projectDetails.sowStatus,
          poStatus: projectDetails.poStatus,
          noteForInvoice: projectDetails.noteForInvoice,
          plannedHours: projectDetails.plannedHours,
          revisedPlannedHours: projectDetails.revisedPlannedHours || 0,
          preSalesStartDate: projectDetails.preSalesStartDate,
          preSalesEndDate: projectDetails.preSalesEndDate,
          actualStartDate: projectDetails.actualStartDate,
          actualEndDate: projectDetails.actualEndDate,
          plannedStartDate: projectDetails.plannedStartDate,
          plannedEndDate: projectDetails.plannedEndDate,
          isClosed: isClosed,
          projectCompletion: projectDetails.projectCompletion
        };

        UpdateProjectDetail(body)
          .then(() => {
            setSnackBarMessage(
              'You’ve updated the project details successfully'
            );
            setSnackbarSeverity('success');
            setSnackBarOpen(true);
            getSingleProjectData();
            // setIsEdit(false);
            toggleConfirm();
            reset();
            dispatch(AppActions.hideLoader());
          })
          .catch((error) => {
            console.log(error)
            setSnackBarMessage(error?.response?.data ? error?.response?.data : 'Unable to update the Project');
            setSnackbarSeverity('error');
            setSnackBarOpen(true);
            dispatch(AppActions.hideLoader());
          });
      }
    }
  };

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  const [isClosed, setIsClosed] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (visibility === 'false' && projectDetails?.isClosed !== true) {
      setIsEdit(true);
    } else {
      setIsEdit(false);
    }
    setIsClosed(projectDetails?.isClosed);
  }, [projectDetails?.isClosed, visibility]);

  const onlyAlphanumericRegex = /[^a-z0-9]/gi;

  const getAllProjectDetails = useCallback(() => {
    getActualHours()
      .then((result) => {
        const allProjects = result.data || [];

        setSelectedProject(
          allProjects.filter((proj) => proj.projectName === projectDetails.name)
        );
      })
      .catch((err) => {});
  }, [projectDetails.name]);

  useEffect(() => {
    getAllProjectDetails();
  }, [getAllProjectDetails]);

  const [selectedProject, setSelectedProject] = useState([]);

  const containsText = (text, searchText) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

  const [searchText, setSearchText] = useState('');

  const displayedEnagagementLead = useMemo(
    () =>
      employees.length > 0 &&
      employees?.filter((option) => containsText(option.name, searchText)),
    [searchText, employees]
  );

  const displayedEnagagementLeadExternal = useMemo(
    () =>
      externalLeads.length > 0 &&
      externalLeads?.filter((option) => containsText(option.name, searchText)),
    [searchText, externalLeads]
  );

  const displayedProductLead = useMemo(
    () =>
      employees.length > 0 &&
      employees?.filter((option) => containsText(option.name, searchText) && !option?.employeeFinance?.isResign),
    [searchText, employees]
  );

  const displayedStakeholderName = useMemo(
    () =>
    stakeholders?.length > 0 &&
    stakeholders?.filter((option) => containsText(option.name, searchText)),
    [searchText, stakeholders]
  );

  const displayedStakeholderRole = useMemo(
    () => 
    stakeholderRoles?.length > 0 &&
    stakeholderRoles?.filter((option) => containsText(option?.value, searchText)),
    [searchText, stakeholderRoles]
  );

  const handleChildPropData = (data) => {
    if (data) {
      navigate('/project-details');
    }
  };

  return (
    <>
      <Grid container direction={'row'} sx={{ py: 2, px: 3 }}>
        <Grid item>
          <Typography
            id="breadcrumbs-edit-project-details"
            onClick={() => {
              navigate('/project-details');
            }}
            style={{ cursor: 'pointer' }}
            variant="h4"
            component="h4"
          >
            {' '}
            Project Details / {' '}
          </Typography>
        </Grid>
        <Grid item>
          <Typography
            pl={0.5}
            style={{ fontWeight: 'normal' }}
            variant="h4"
            component="h4"
          >
            {projectDetails.name}
          </Typography>
        </Grid>
      </Grid>
      <Container maxWidth="xl">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12}>
            <Card>
              <Grid container sx={{ justifyContent: 'space-between' }}>
                <Grid
                  className="project-chart-wrapper"
                  item
                  xs={4}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start'
                  }}
                >
                  <Grid
                    className="project-image-wrapper"
                    item
                    sx={{ pl: 4, py: 3, pr: 4 }}
                  >
                    <Grid
                      item
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center'
                      }}
                    >
                      <img
                        alt="logo"
                        style={{ height: 70 }}
                        src={process.env.PUBLIC_URL + '/img/user.png'}
                      />
                      <Grid item sx={{ pl: 2 }}>
                        <Typography variant="h4">
                          {projectDetails.name}
                        </Typography>
                        <Typography
                          variant="h5"
                          style={{ fontWeight: 'normal', paddingTop: 5 }}
                        >
                          {projectDetails.key}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    className="project-image-wrapper"
                    item
                    sx={{ pl: 4, py: 3, pr: 4 }}
                  >
                    <Grid
                      item
                      display="flex"
                      sx={{ mb: 1 }}
                      justifyContent="space-between "
                    >
                      <Typography variant="h4" style={{ fontSize: '12px' }}>
                        Segment
                      </Typography>
                      <Typography
                        variant="h4"
                        color="GrayText"
                        style={{ fontSize: '12px' }}
                      >
                        {segmentData?.name}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      display="flex"
                      sx={{ mb: 1 }}
                      justifyContent="space-between"
                    >
                      <Typography variant="h4" style={{ fontSize: '12px' }}>
                        Engagement Lead
                      </Typography>
                      <Tooltip
                        arrow
                        title={projectDetails?.customer?.engagementLead}
                        placement="bottom-end"
                      >
                        <Typography
                          variant="h4"
                          color="GrayText"
                          style={{ fontSize: '12px' }}
                        >
                          {changeStringLength(
                            projectDetails?.customer?.engagementLead,
                            18
                          )
                            ? changeStringLength(
                                projectDetails?.customer?.engagementLead,
                                18
                              )
                            : projectDetails?.customer?.engagementLead}
                        </Typography>
                      </Tooltip>
                    </Grid>
                    <Grid item display="flex" justifyContent="space-between">
                      <Typography variant="h4" style={{ fontSize: '12px' }}>
                        Project Status
                      </Typography>
                      <Typography
                        variant="h4"
                        color="GrayText"
                        style={{ fontSize: '12px' }}
                      >
                        {projectDetails.projectStatus || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    className="project-image-wrapper"
                    item
                    sx={{ pl: 4, py: 3, pr: 4 }}
                  >
                    <Grid
                      item
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center'
                      }}
                    >
                      <Stack
                        sx={{ width: '100%', color: 'grey.500' }}
                        spacing={2}
                      >
                        <Grid sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Grid
                            style={{ paddingBottom: 3 }}
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between'
                            }}
                          >
                            <Typography
                              className="progress-text"
                              style={{ fontWeight: 'normal', color: '#232C68' }}
                              variant="body1"
                            >
                              Project Completion
                            </Typography>
                            <Typography
                              className="progress-text"
                              style={{ fontWeight: 'normal', color: '#232C68' }}
                              variant="body1"
                            >
                              {`${projectDetails.projectCompletion}%`}
                            </Typography>
                          </Grid>
                          <LinearProgress
                            variant="determinate"
                            value={projectDetails.projectCompletion}
                            color={
                              projectDetails.projectCompletion > 100
                                ? 'error'
                                : 'primary'
                            }
                          />
                        </Grid>
                        <Grid
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            pt: 1
                          }}
                        >
                          <Grid
                            style={{ paddingBottom: 3 }}
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between'
                            }}
                          >
                            <Typography
                              className="progress-text"
                              style={{ fontWeight: 'normal', color: '#232C68' }}
                              variant="body1"
                            >
                              Project Actual Effort Status
                            </Typography>
                            <Typography
                              className="progress-text"
                              style={{ fontWeight: 'normal', color: '#232C68' }}
                              variant="body1"
                            >
                              {`${projectDetails.actualEffectStatus}%`}
                            </Typography>
                          </Grid>
                          <LinearProgress
                            variant="determinate"
                            value={projectDetails.actualEffectStatus}
                            color={
                              projectDetails.actualEffectStatus > 100
                                ? 'error'
                                : 'primary'
                            }
                          />
                        </Grid>
                        <Grid
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            pt: 1
                          }}
                        >
                          <Grid
                            style={{ paddingBottom: 3 }}
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between'
                            }}
                          >
                            <Typography
                              className="progress-text"
                              style={{ fontWeight: 'normal', color: '#232C68' }}
                              variant="body1"
                            >
                              Remaining Effort
                            </Typography>
                            <Typography
                              className="progress-text"
                              style={{ fontWeight: 'normal', color: '#232C68' }}
                              variant="body1"
                            >
                              {`${projectDetails.actualRemainingEffort}%`}
                            </Typography>
                          </Grid>
                          <LinearProgress
                            variant="determinate"
                            value={projectDetails.actualRemainingEffort}
                            color={
                              projectDetails.actualRemainingEffort > 100
                                ? 'error'
                                : 'primary'
                            }
                          />
                        </Grid>
                      </Stack>
                    </Grid>
                  </Grid>
                  <Grid className="" item sx={{ pl: 4, py: 3, pr: 4 }}>
                    <Typography
                      variant="h4"
                      sx={{ pt: 1, pb: 3, fontSize: 12 }}
                    >
                      Project Actual Hours Distribution
                    </Typography>
                    <Grid
                      item
                      sx={{ display: 'flex', flexDirection: 'column' }}
                    >
                      <Grid
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Grid sx={{ display: 'flex', flexDirection: 'row' }}>
                          <Grid style={{ width: '50%' }}>
                            <PieChart
                              data={[
                                {
                                  title: 'Ba',
                                  value:
                                    selectedProject[0]?.bAhours === 0 ||
                                    !selectedProject[0]
                                      ? 1
                                      : selectedProject[0]?.bAhours,
                                  color: '#DA4C61'
                                },
                                {
                                  title: 'Dev',
                                  value:
                                    selectedProject[0]?.deVhours === 0 ||
                                    !selectedProject[0]
                                      ? 1
                                      : selectedProject[0]?.deVhours,
                                  color: '#A1D0C9'
                                },
                                {
                                  title: 'PM',
                                  value:
                                    selectedProject[0]?.pmHours === 0 ||
                                    !selectedProject[0]?.pmHours
                                      ? 1
                                      : selectedProject[0]?.pmHours,
                                  color: '#ECC44D'
                                },
                                {
                                  title: 'QA',
                                  value:
                                    selectedProject[0]?.qAhours === 0 ||
                                    !selectedProject[0]
                                      ? 1
                                      : selectedProject[0]?.qAhours,
                                  color: '#6C898F'
                                },
                                {
                                  title: 'SM',
                                  value:
                                    selectedProject[0]?.sMhours === 0 ||
                                    !selectedProject[0]
                                      ? 1
                                      : selectedProject[0]?.sMhours,
                                  color: '#3C3D51'
                                },
                                {
                                  title: 'Others',
                                  value:
                                    selectedProject[0]?.otherhours === 0 ||
                                    !selectedProject[0]
                                      ? 1
                                      : selectedProject[0]?.otherhours,
                                  color: '#E8DED3'
                                }
                              ]}
                              lineWidth={40}
                            />
                          </Grid>
                          <Grid
                            style={{ width: '50%' }}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              paddingLeft: 3,
                              justifyContent: 'space-evenly'
                            }}
                          >
                            <Grid
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between'
                              }}
                            >
                              <Grid
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center'
                                }}
                              >
                                <span
                                  className="circle"
                                  style={{ backgroundColor: '#DA4C61' }}
                                ></span>
                                <span className="circle-text">BA</span>
                              </Grid>
                              <span className="circle-hours">
                                {selectedProject[0]?.bAhours || 0} hrs
                              </span>
                            </Grid>
                            <Grid
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between'
                              }}
                            >
                              <Grid
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center'
                                }}
                              >
                                <span
                                  className="circle"
                                  style={{ backgroundColor: '#A1D0C9' }}
                                ></span>
                                <span className="circle-text">DEV</span>
                              </Grid>
                              <span className="circle-hours">
                                {selectedProject[0]?.deVhours || 0} hrs
                              </span>
                            </Grid>
                            <Grid
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between'
                              }}
                            >
                              <Grid
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center'
                                }}
                              >
                                <span
                                  className="circle"
                                  style={{ backgroundColor: '#ECC44D' }}
                                ></span>
                                <span className="circle-text">PM</span>
                              </Grid>
                              <span className="circle-hours">
                                {selectedProject[0]?.pmHours || 0} hrs
                              </span>
                            </Grid>
                            <Grid
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between'
                              }}
                            >
                              <Grid
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center'
                                }}
                              >
                                <span
                                  className="circle"
                                  style={{ backgroundColor: '#6C898F' }}
                                ></span>
                                <span className="circle-text">QA</span>
                              </Grid>
                              <span className="circle-hours">
                                {selectedProject[0]?.qAhours || 0} hrs
                              </span>
                            </Grid>
                            <Grid
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between'
                              }}
                            >
                              <Grid
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center'
                                }}
                              >
                                <span
                                  className="circle"
                                  style={{ backgroundColor: '#E8DED3' }}
                                ></span>
                                <span className="circle-text">SM</span>
                              </Grid>
                              <span className="circle-hours">
                                {selectedProject[0]?.sMhours || 0} hrs
                              </span>
                            </Grid>
                            {/* <Grid
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between'
                              }}
                            >
                              <Grid
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center'
                                }}
                              >
                                <span
                                  className="circle"
                                  style={{ backgroundColor: '#3C3D51' }}
                                ></span>
                                <span className="circle-text">DEC</span>
                              </Grid>
                              <span className="circle-hours">{selectedProject[0]?.bAhours}</span>
                            </Grid> */}
                            <Grid
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between'
                              }}
                            >
                              <Grid
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center'
                                }}
                              >
                                <span
                                  className="circle"
                                  style={{ backgroundColor: '#A19876' }}
                                ></span>
                                <span className="circle-text">Others</span>
                              </Grid>
                              <span className="circle-hours">
                                {selectedProject[0]?.otherhours || 0} hrs
                              </span>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          pt: 1,
                          fontSize: 10,
                          justifyContent: 'space-between'
                        }}
                      >
                        <span>
                          Total Planned -{' '}
                          {selectedProject[0]?.plannedHours || 0} hrs
                        </span>
                        <span>
                          {' '}
                          Total Spent -{' '}
                          {selectedProject[0]?.totalWorkedHours || 0} hrs
                        </span>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={8}
                  style={{
                    overflow: 'auto',
                    maxHeight: '90vh',
                    paddingBottom: '40px',
                    paddingTop: '32px',
                    paddingLeft: '40px',
                    paddingRight: '40px'
                  }}
                >
                  {/* <form onSubmit={handleSubmit(onSubmit)}> */}
                  <Grid
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'flex-end'
                    }}
                  >
                    <Typography variant="h4" style={{ fontSize: '14px' }}>
                      General - Details
                    </Typography>
                    <Grid item>
                      <Button
                        id="btn-project-details-teams"
                        sx={{
                          marginRight: 2,

                          fontSize: '0.7rem',
                          alignItems: 'center',
                          fontWeight: 'normal'
                        }}
                        variant="outlined"
                        disabled={projectDetails?.isClosed}
                        onClick={() => {
                          if(isEdit) {
                            navigate(
                              `/project-details/teams/${projectDetails.id}?view=true`
                            )
                          }else {
                            navigate(
                              `/project-details/teams/${projectDetails.id}?view=false`
                            )
                          }
                        }}
                      >
                        <PeopleOutlineIcon
                          sx={{ marginRight: 1, fontSize: '14px' }}
                          fontSize={'inherit'}
                        />
                        Team
                      </Button>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ pt: 2 }}>
                    <Grid item xs={6}>
                      <Typography style={{ fontSize: '13px' }} color="GrayText">
                        Client <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <FormControl fullWidth>
                        <TextField
                          id="edit-customer"
                          inputProps={{ style: { fontSize: '13px' } }}
                          disabled
                          size="small"
                          // {...register('customer', {})}
                          placeholder="Client Name"
                          variant="outlined"
                          value={projectDetails?.customer?.name}
                          onChange={(e) => {
                            setProjectDetails((prev) => ({
                              ...prev,
                              customer: e.target.value
                            }));
                            setValidationErrors((prev) => ({
                              ...prev,
                              customerError: ''
                            }));
                            // setValue('customer', e.target.value);
                          }}
                        />
                        {validationErrors.customerError && (
                          <Typography
                            style={{
                              fontSize: '11px'
                            }}
                            color="red"
                          >
                            {validationErrors.customerError}&nbsp;
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Project Type <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        {!isEdit ? (
                          <TextField
                            id="type"
                            disabled={!isEdit}
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            placeholder="Project Type"
                            variant="outlined"
                            value={projectDetails.type}
                          />
                        ) : (
                          <Select
                            id="edit-type"
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Project Type' && !field.edit
                              )
                            }
                            className="select-text"
                            size="small"
                            displayEmpty
                            inputProps={{ 'aria-label': 'Without label' }}
                            value={projectDetails.type || ''}
                            onChange={(e) => {
                              setProjectDetails((prev) => ({
                                ...prev,
                                type: e.target.value
                              }));
                              setValidationErrors((prev) => ({
                                ...prev,
                                typeError: ''
                              }));
                              // setValue('type', e.target.value);
                            }}
                          >
                            <MenuItem value="Pre-sale">Pre-sale</MenuItem>
                            <MenuItem value="Delivery">Delivery</MenuItem>
                          </Select>
                        )}
                        {validationErrors.typeError && (
                          <Typography
                            style={{
                              fontSize: '11px'
                            }}
                            color="red"
                          >
                            {validationErrors.typeError}&nbsp;
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    {projectDetails.type === 'Pre-sale' && (
                      <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Pre-sale Stage <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        {!isEdit ? (
                          <TextField
                            id="preSaleStage"
                            disabled={!isEdit}
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            placeholder="Pre-sale Stage"
                            variant="outlined"
                            value={projectDetails.salesStage}
                          />
                        ) : (
                          <Select
                            id="edit-preSaleStage"
                            className="select-text"
                            size="small"
                            displayEmpty
                            inputProps={{ 'aria-label': 'Without label' }}
                            value={projectDetails.salesStage || ''}
                            onChange={(e) => {
                              setProjectDetails((prev) => ({
                                ...prev,
                                salesStage: e.target.value
                              }));
                              setValidationErrors((prev) => ({
                                ...prev,
                                preSaleStageError: ''
                              }));
                              // setValue('type', e.target.value);
                            }}
                          >
                          {saleStages?.length > 0 &&
                          saleStages.map((item) => (
                          <MenuItem key={item.id} value={item.name}>
                          {item.name}
                          </MenuItem>
                          ))}
                          </Select>
                        )}
                        {validationErrors.preSaleStageError && (
                          <Typography
                            style={{
                              fontSize: '11px'
                            }}
                            color="red"
                          >
                            {validationErrors.preSaleStageError}&nbsp;
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    )}
                    <Grid item xs={6}>
                      <Typography style={{ fontSize: '13px' }} color="GrayText">
                        Project Name <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <FormControl fullWidth>
                        <TextField
                          id="name"
                          inputProps={{ style: { fontSize: '13px' } }}
                          disabled
                          size="small"
                          // {...register('name', {})}
                          placeholder="Project Name"
                          variant="outlined"
                          value={projectDetails.name}
                          onChange={(e) => {
                            setProjectDetails((prev) => ({
                              ...prev,
                              name: e.target.value
                            }));
                            setValidationErrors((prev) => ({
                              ...prev,
                              nameError: ''
                            }));
                            // setValue('name', e.target.value);
                          }}
                        />
                        {validationErrors.nameError && (
                          <Typography
                            style={{
                              fontSize: '11px'
                            }}
                            color="red"
                          >
                            {validationErrors.nameError}&nbsp;
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Project Code <span style={{ color: 'red' }}>*</span>
                        </Typography>

                        <TextField
                          id="key"
                          size="small"
                          inputProps={{ style: { fontSize: '13px' } }}
                          className="input-text"
                          // {...register('key')}
                          value={projectDetails.key}
                          onChange={(e) => {
                            e.target.value = e.target.value.replace(
                              onlyAlphanumericRegex,
                              ''
                            );
                            if (e.target.value.length > 5) {
                              setValidationErrors((prev) => ({
                                ...prev,
                                keyError: 'Character length should not exceed 5'
                              }));
                            } else {
                              setValidationErrors((prev) => ({
                                ...prev,
                                keyError: ''
                              }));
                            }
                            setProjectDetails((prev) => ({
                              ...prev,
                              key: e.target.value.toUpperCase()
                            }));
                            // setValue('key', e.target.value);
                          }}
                          fullWidth
                          placeholder="Project Code"
                          variant="outlined"
                          disabled
                        />
                        {validationErrors.keyError && (
                          <Typography
                            style={{
                              fontSize: '11px'
                            }}
                            color="red"
                          >
                            {validationErrors.keyError}&nbsp;
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Project Nature <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        {!isEdit ? (
                          <TextField
                            id="nature"
                            disabled={!isEdit}
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            fullWidth
                            variant="outlined"
                            value={projectDetails.nature}
                          />
                        ) : (
                          <Select
                            id="edit-nature"
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Project Nature' && !field.edit
                              )
                            }
                            className="select-text"
                            size="small"
                            displayEmpty
                            inputProps={{ 'aria-label': 'Without label' }}
                            // {...register('nature', {})}
                            value={projectDetails.nature}
                            onChange={(e) => {
                              setProjectDetails((prev) => ({
                                ...prev,
                                nature: e.target.value
                              }));
                              setValidationErrors((prev) => ({
                                ...prev,
                                natureError: ''
                              }));
                              // setValue('nature', e.target.value);
                            }}
                          >
                            {projectNatures?.length > 0 &&
                              projectNatures.map((nature) => (
                                <MenuItem key={nature.id} value={nature.name}>
                                  {nature.name}
                                </MenuItem>
                              ))}
                          </Select>
                        )}
                        {validationErrors.natureError && (
                          <Typography
                            style={{
                              fontSize: '11px'
                            }}
                            color="red"
                          >
                            {validationErrors.natureError}&nbsp;
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Project Status <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        {!isEdit ? (
                          <TextField
                            id="projectStatus"
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Project Status' && !field.edit
                              )
                            }
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            placeholder="Project Status"
                            variant="outlined"
                            value={projectDetails.projectStatus}
                          />
                        ) : (
                          <Select
                            id="edit-projectStatus"
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Project Status' && !field.edit
                              )
                            }
                            className="select-text"
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            displayEmpty
                            // {...register('projectStatus', {})}
                            value={projectDetails.projectStatus || ''}
                            onChange={(e) => {
                              setProjectDetails((prev) => ({
                                ...prev,
                                projectStatus: e.target.value
                              }));
                              setValidationErrors((prev) => ({
                                ...prev,
                                projectStatusError: ''
                              }));
                              // setValue('projectStatus', e.target.value);
                            }}
                          >
                            {projectStatus.length > 0 &&
                              projectStatus.map((stat) => (
                                <MenuItem key={stat.id} value={stat.name}>
                                  {stat.name}
                                </MenuItem>
                              ))}
                          </Select>
                        )}
                        {validationErrors.projectStatusError && (
                          <Typography
                            style={{
                              paddingLeft: 15,
                              paddingRight: 15,
                              fontSize: '11px'
                            }}
                            color="red"
                          >
                            {validationErrors.projectStatusError}&nbsp;
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    {/* <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Engagement Lead Type{' '}
                          <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        {!isEdit ? (
                          <TextField
                            id="leadType"
                            disabled={!isEdit}
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            fullWidth
                            variant="outlined"
                            value={projectDetails.leadType}
                          />
                        ) : (
                          <Select
                            id="edit-leadType"
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Lead Resource Type' &&
                                  !field.edit
                              )
                            }
                            className="select-text"
                            size="small"
                            displayEmpty
                            inputProps={{ 'aria-label': 'Without label' }}
                            // {...register('leadType', {})}
                            value={projectDetails.leadType}
                            onChange={(e) => {
                              setProjectDetails((prev) => ({
                                ...prev,
                                leadType: e.target.value
                              }));
                              setValidationErrors((prev) => ({
                                ...prev,
                                leadTypeError: ''
                              }));
                              // setValue('leadType', e.target.value);
                            }}
                          >
                            <MenuItem
                              style={{ display: 'none' }}
                              selected
                              disabled
                              value=""
                            >
                              Select Engagement Lead Type
                            </MenuItem>
                            <MenuItem value="Internal">Internal</MenuItem>
                            <MenuItem value="External">External</MenuItem>
                          </Select>
                        )}
                      </FormControl>

                      {validationErrors.leadTypeError && (
                        <Typography
                          style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            fontSize: '11px'
                          }}
                          color="red"
                        >
                          {validationErrors.leadTypeError}&nbsp;
                        </Typography>
                      )}
                    </Grid> */}
                    {/* <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Engagement Lead{' '}
                          <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        {!isEdit ? (
                          <TextField
                            id="leadName"
                            disabled={!isEdit}
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            fullWidth
                            variant="outlined"
                            value={projectDetails.leadName}
                          />
                        ) : (
                          <Select
                            id="leadId"
                            MenuProps={{ autoFocus: false }}
                            labelId="search-select-label"
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Lead Resource Name' &&
                                  !field.edit
                              )
                            }
                            className="select-text"
                            size="small"
                            displayEmpty
                            inputProps={{ 'aria-label': 'Without label' }}
                            // {...register('leadId', {})}
                            defaultValue={''}
                            value={projectDetails.leadId}
                            onChange={(e) => {
                              if (projectDetails.leadType === 'External') {
                                const leadId = e.target.value;
                                const selectObj = externalLeads?.filter(
                                  (lead) => lead.id === leadId
                                )[0];
                                const leadName = selectObj.name;
                                setProjectDetails((prev) => ({
                                  ...prev,
                                  leadId: leadId
                                }));
                                setProjectDetails((prev) => ({
                                  ...prev,
                                  leadName: leadName
                                }));
                              } else {
                                const empId = e.target.value;
                                const selectObj = employees?.filter(
                                  (emp) => emp.id === empId
                                )[0];
                                const empName = selectObj.name;
                                setProjectDetails((prev) => ({
                                  ...prev,
                                  leadId: empId
                                }));
                                setProjectDetails((prev) => ({
                                  ...prev,
                                  leadName: empName
                                }));
                              }
                              setValidationErrors((prev) => ({
                                ...prev,
                                leadNameError: ''
                              }));
                            }}
                            onClose={() => setSearchText('')}
                          >
                            <ListSubheader>
                              <TextField
                                id="search-leadId"
                                size="small"
                                autoFocus
                                placeholder="Type to search..."
                                fullWidth
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon />
                                    </InputAdornment>
                                  )
                                }}
                                onChange={(e) => setSearchText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Escape') {
                                    e.stopPropagation();
                                  }
                                }}
                              ></TextField>
                            </ListSubheader>
                            <MenuItem
                              style={{ display: 'none' }}
                              selected
                              disabled
                              value=""
                            >
                              Select Engagement Lead
                            </MenuItem>
                            {projectDetails.leadType === 'Internal'
                              ? displayedEnagagementLead.length > 0 &&
                                displayedEnagagementLead?.map((option, i) => (
                                  <MenuItem key={i} value={option.id}>
                                    {option.name}
                                  </MenuItem>
                                ))
                              : displayedEnagagementLeadExternal?.length > 0 &&
                                displayedEnagagementLeadExternal?.map(
                                  (option, i) => (
                                    <MenuItem key={i} value={option.id}>
                                      {option.name}
                                    </MenuItem>
                                  )
                                )}
                          </Select>
                        )}
                      </FormControl>

                      {validationErrors.leadNameError && (
                        <Typography
                          style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            fontSize: '11px'
                          }}
                          color="red"
                        >
                          {validationErrors.leadNameError}&nbsp;
                        </Typography>
                      )}
                    </Grid> */}
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Project Lead <span style={{ color: 'red' }}>*</span>
                        </Typography>

                        <Select
                          id="projectLeadId"
                          MenuProps={{ autoFocus: false }}
                          labelId="search-select-label"
                          disabled={
                            !isEdit ||
                            fields.some(
                              (field) =>
                                field.name === 'Project Lead' && !field.edit
                            )
                          }
                          className="select-text"
                          size="small"
                          displayEmpty
                          inputProps={{ 'aria-label': 'Without label' }}
                          value={projectDetails.projectLeadId}
                          onChange={(e) => {
                            setProjectDetails((prev) => ({
                              ...prev,
                              projectLeadId: e.target.value
                            }));
                            setValidationErrors((prev) => ({
                              ...prev,
                              projectLeadError: ''
                            }));
                          }}
                          onClose={() => setSearchText('')}
                        >
                          <ListSubheader>
                            <TextField
                              id="search-projectLeadId"
                              size="small"
                              autoFocus
                              placeholder="Type to search..."
                              fullWidth
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon />
                                  </InputAdornment>
                                )
                              }}
                              onChange={(e) => setSearchText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key !== 'Escape') {
                                  e.stopPropagation();
                                }
                              }}
                            ></TextField>
                          </ListSubheader>
                          <MenuItem
                            style={{ display: 'none' }}
                            selected
                            disabled
                            value=""
                          >
                            Select Project Lead
                          </MenuItem>
                          {displayedProductLead.length > 0 &&
                            displayedProductLead?.map((option, i) => (
                              <MenuItem key={i} value={option.id}>
                                {option.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      {validationErrors.projectLeadError && (
                        <Typography
                          style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            fontSize: '11px'
                          }}
                          color="red"
                        >
                          {validationErrors.projectLeadError}&nbsp;
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Project Completion (%)
                        </Typography>
                        <TextField
                          id="projectCompletion"
                          disabled={
                            !isEdit ||
                            fields.some(
                              (field) =>
                                field.name === 'Project Completion' &&
                                !field.edit
                            )
                          }
                          inputProps={{ style: { fontSize: '13px' } }}
                          size="small"
                          type="number"
                          className="input-text"
                          // {...register('projectCompletion', {})}
                          value={projectDetails.projectCompletion}
                          onChange={(e) => {
                            if (e.target.value.length <= 20) {
                              let value = parseInt(e.target.value, 10);
                              if (value > 100) value = 100;

                              setProjectDetails((prev) => ({
                                ...prev,
                                projectCompletion: value
                              }));
                              setValidationErrors((prev) => ({
                                ...prev,
                                projectCompletionError: ''
                              }));
                            }
                          }}
                          fullWidth
                          placeholder="Enter Project Completion"
                          variant="outlined"
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Typography
                    variant="h4"
                    sx={{ pt: 4 }}
                    style={{ fontSize: '14px' }}
                  >
                    Stakeholders
                  </Typography>
                  {isEdit ? (
                    <Grid container spacing={2} sx={{ pt: 2 }}>
                      <Grid item xs={6}>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Stakeholder <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        {!isEdit ? (
                          <TextField
                            id="stakeholder"
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Stakeholder' && !field.edit
                              )
                            }
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            fullWidth
                            placeholder="Project Stakeholder"
                            variant="outlined"
                          />
                        ) : (
                          <Select
                            id="edit-stakeholder"
                            MenuProps={{ autoFocus: false }}
                            labelId="search-select-label"
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Stakeholder' && !field.edit
                              )
                            }
                            className="select-text"
                            fullWidth
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            displayEmpty
                            defaultValue={''}
                            value={selectedStakeholder || ''}
                            onChange={(e) => {
                              setSelectedStakeholder(e.target.value);
                              isSetStakeholderName(true);
                              setValidationErrors((prev) => ({
                                ...prev,
                                projectStakeHoldersError: ''
                              }));
                            }}
                            onClose={() => setSearchText('')}
                          >
                            <ListSubheader>
                              <TextField
                                id="search-stakeholder"
                                size="small"
                                autoFocus
                                placeholder="Type to search..."
                                fullWidth
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon />
                                    </InputAdornment>
                                  )
                                }}
                                onChange={(e) => setSearchText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Escape') {
                                    e.stopPropagation();
                                  }
                                }}
                              ></TextField>
                            </ListSubheader>
                            <MenuItem
                              style={{ display: 'none' }}
                              selected
                              disabled
                              value={''}
                            >
                              Select Stakeholder
                            </MenuItem>
                            {displayedStakeholderName?.length > 0 &&
                              displayedStakeholderName?.map((option, i) => (
                                <MenuItem key={i} value={option.id}>
                                  {option.name}
                                </MenuItem>
                              ))}
                          </Select>
                        )}
                        <Typography
                          style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            fontSize: '11px',
                            textTransform: 'capitalize'
                          }}
                          color="red"
                        >
                          {validationErrors.projectStakeHoldersError}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Stakeholder Role{' '}
                          <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        {!isEdit ? (
                          <TextField
                            id="stakeholderRole"
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Stakeholder' && !field.edit
                              )
                            }
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            fullWidth
                            placeholder="Project Stakeholder Role"
                            variant="outlined"
                          />
                        ) : (
                          <Select
                            id="edit-stakeholderRole"
                            MenuProps={{ autoFocus: false }}
                            labelId="search-select-label"
                            style={{width: '110%'}}
                            size="small"
                            displayEmpty
                            inputProps={{ 'aria-label': 'Without label' }}
                            defaultValue={''}
                            value={selectedRole || ''}
                            onChange={(e) => {
                              setSelectedRole(e.target.value);
                              isSetStakeholderRole(true);
                            }}
                            onClose={() => setSearchText('')}
                          >
                            <ListSubheader>
                              <TextField
                                id="search-stakeholderRole"
                                size="small"
                                autoFocus
                                placeholder="Type to search..."
                                fullWidth
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon />
                                    </InputAdornment>
                                  )
                                }}
                                onChange={(e) => setSearchText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Escape') {
                                    e.stopPropagation();
                                  }
                                }}
                              ></TextField>
                            </ListSubheader>
                            <MenuItem
                              style={{ display: 'none' }}
                              selected
                              disabled
                              value={''}
                            >
                              Select Stakeholder Role
                            </MenuItem>
                            {displayedStakeholderRole?.length > 0 &&
                              displayedStakeholderRole?.map((option, i) => (
                                <MenuItem key={i} value={option?.id}>
                                  {option?.value}
                                </MenuItem>
                              ))}
                          </Select>
                        )}
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          id="projectStakeHolders"
                          disabled={
                            !isEdit ||
                            fields.some(
                              (field) =>
                                field.name === 'Stakeholder' && !field.edit
                            ) ||
                            !(isStakeholderName && isStakeholderRole)
                          }
                          type="button"
                          sx={{
                            ml: 2,
                            px: 4,
                            mt: 2.5,
                            fontSize: '13px',
                            fontWeight: 'normal'
                          }}
                          variant="outlined"
                          onClick={() => {
                            if (
                              projectDetails.projectStakeHolders.some(
                                (sta) =>
                                  sta?.stakeholder?.id ===
                                    selectedStakeholder ||
                                  sta.stakeholderId === selectedStakeholder
                              )
                            ) {
                              setValidationErrors((prev) => ({
                                ...prev,
                                stakeholderDuplicateError:
                                  'Duplicated stakeholders'
                              }));
                            } else if (
                              selectedStakeholder !== '' &&
                              selectedRole !== ''
                            ) {
                              setValidationErrors((prev) => ({
                                ...prev,
                                stakeholderDuplicateError: ''
                              }));
                              const stakeholdersArr = [
                                ...projectDetails.projectStakeHolders
                              ];
                              const stakeholdersArrLocal = [
                                ...selectedStakeholders
                              ];
                              const filteredStakeholder = stakeholders?.filter(
                                (stake) => stake.id === selectedStakeholder
                              )[0];

                              const filteredStakeholderRole = stakeholderRoles?.filter(
                                (stake) => stake.id === selectedRole
                              )[0];

                              const currentStakeholder = {
                                stakeholderId: filteredStakeholder.id,
                                stakeholderRoleId: filteredStakeholderRole.id,
                                stakeholderRole: filteredStakeholderRole.value
                              };

                              const currentStakeholderLocal = {
                                stakeholderId: filteredStakeholder?.id,
                                stakeholderName: filteredStakeholder?.name,
                                stakeholderRoleId: filteredStakeholderRole.id,
                                stakeholderRole: filteredStakeholderRole.value
                              };
                              stakeholdersArr.push(currentStakeholder);
                              stakeholdersArrLocal.push(
                                currentStakeholderLocal
                              );
                              setProjectDetails((prev) => ({
                                ...prev,
                                projectStakeHolders: [...stakeholdersArr]
                              }));
                              setSelectedStakeholders([
                                ...stakeholdersArrLocal
                              ]);
                              setSelectedRole('');
                              setSelectedStakeholder('');
                            }
                          }}
                        >
                          Add
                        </Button>
                        <Typography
                          style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            fontSize: '11px',
                            textTransform: 'capitalize'
                          }}
                          color="red"
                        >
                          {validationErrors.stakeholderDuplicateError}
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid style={{ marginTop: -30 }} />
                  )}
                  {projectDetails?.projectStakeHolders?.length > 0 && (
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        pt: 2,
                        pb: 1,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      {!isEdit ? (
                        <Grid style={{ display: 'flex'}}>
                          <Grid item xs={6}>
                          <Typography
                          style={{
                            fontSize: '13px',
                            marginTop: '2rem',
                            marginLeft: '1rem',
                            marginBottom: '-3rem'
                          }}
                          color="GrayText"
                        >
                          Stakeholder 
                          <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        </Grid>
                        <Grid item xs={6}>
                        <Typography
                          style={{
                            fontSize: '13px',
                            marginTop: '2rem',
                            marginLeft: '1rem',
                            marginBottom: '-3rem'
                          }}
                          color="GrayText"
                        >
                          Stakeholder Role 
                          <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        </Grid>
                        </Grid>
                      ) : (
                        <></>
                      )}
                      {selectedStakeholders?.map((stake, i) => (
                        <Grid
                          item
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Grid item xs={6} sx={{ pt: 2, mr: 2 }}>
                            <TextField
                              id={"edit-stakeholderName" + i}
                              disabled
                              size="small"
                              fullWidth
                              variant="outlined"
                              className="input-text"
                              inputProps={{
                                style: {
                                  height: '1.25rem',
                                  fontSize: '13px'
                                }
                              }}
                              value={stake.stakeholderName}
                            />
                          </Grid>
                          <Grid item xs={4} sx={{ pt: 2 }}>
                            <TextField
                              id={"edit-stakeholderRole" + i}
                              disabled
                              size="small"
                              fullWidth
                              variant="outlined"
                              className="input-text"
                              inputProps={{
                                style: {
                                  height: '1.25rem',
                                  fontSize: '13px'
                                }
                              }}
                              value={stake.stakeholderRole}
                            />
                          </Grid>
                          <Grid item xs={2} sx={{ pt: 2.5, ml: 1 }}>
                            {isEdit ? (
                              <CancelIcon
                                id={"remove-stakeholder" + i}
                                style={{
                                  color: 'gray',
                                  fontSize: '1.25rem',
                                  cursor: 'pointer'
                                }}
                                onClick={() => {
                                  const stakeholdersArr = [
                                    ...projectDetails.projectStakeHolders
                                  ];
                                  const stakeholdersArrLocal = [
                                    ...selectedStakeholders
                                  ];

                                  const filteredStakeholders =
                                    stakeholdersArr?.filter((st) => {
                                      const filterVal = st?.stakeholderId
                                        ? st?.stakeholderId
                                        : st?.stakeholder?.id;

                                      return filterVal !== stake?.stakeholderId;
                                    });

                                  const filteredStakeholdersLocal =
                                    stakeholdersArrLocal?.filter(
                                      (st) =>
                                        st.stakeholderId !== stake.stakeholderId
                                    );

                                  setSelectedStakeholders([
                                    ...filteredStakeholdersLocal
                                  ]);
                                  setProjectDetails((prev) => ({
                                    ...prev,
                                    projectStakeHolders: [
                                      ...filteredStakeholders
                                    ]
                                  }));
                                }}
                              />
                            ) : (
                              <></>
                            )}
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                  <Typography
                    variant="h4"
                    sx={{ pt: 4, pb: 2 }}
                    style={{ fontSize: '14px' }}
                  >
                    Financial Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Billing Category
                        </Typography>
                        {!isEdit ? (
                          <TextField
                            id="billingCategory"
                            disabled
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            placeholder="Project Type"
                            variant="outlined"
                            value={projectDetails.billingCategory}
                          />
                        ) : (
                          <Select
                            id="edit-billingCategory"
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Project Billing Category' &&
                                  !field.edit
                              )
                            }
                            className="select-text"
                            size="small"
                            displayEmpty
                            inputProps={{ style: { fontSize: '13px' } }}
                            value={projectDetails.billingCategory}
                            onChange={(e) => {
                              setProjectDetails((prev) => ({
                                ...prev,
                                billingCategory: e.target.value
                              }));
                              setValidationErrors((prev) => ({
                                ...prev,
                                billingCategoryError: ''
                              }));
                            }}
                          >
                            {billingCategories.length > 0 &&
                              billingCategories?.map((bill) => (
                                <MenuItem value={bill.name}>
                                  {bill.name}
                                </MenuItem>
                              ))}
                          </Select>
                        )}
                        {validationErrors.billingCategoryError && (
                          <Typography
                            style={{
                              paddingLeft: 15,
                              paddingRight: 15,
                              fontSize: '11px',
                              textTransform: 'capitalize'
                            }}
                            color="red"
                          >
                            {validationErrors.billingCategoryError}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          SOW / PO Status{' '}
                          <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        {!isEdit ? (
                          <TextField
                            id="sowStatus"
                            disabled={!isEdit}
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            placeholder="Project Type"
                            variant="outlined"
                            value={projectDetails.sowStatus}
                          />
                        ) : (
                          <Select
                            id="edit-sowStatus"
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'SOW Status' && !field.edit
                              )
                            }
                            className="select-text"
                            size="small"
                            displayEmpty
                            inputProps={{ style: { fontSize: '13px' } }}
                            value={projectDetails.sowStatus}
                            onChange={(e) => {
                              setProjectDetails((prev) => ({
                                ...prev,
                                sowStatus: e.target.value
                              }));
                              setValidationErrors((prev) => ({
                                ...prev,
                                sowStatusError: ''
                              }));
                            }}
                          >
                            <MenuItem value="Approved">Approved</MenuItem>
                            <MenuItem value="Raised">Raised</MenuItem>
                            <MenuItem value="Not raised">Not raised</MenuItem>
                            <MenuItem value="N/A">N/A</MenuItem>
                          </Select>
                        )}
                        {validationErrors.sowStatusError && (
                          <Typography
                            style={{
                              paddingLeft: 15,
                              paddingRight: 15,
                              fontSize: '11px',
                              textTransform: 'capitalize'
                            }}
                            color="red"
                          >
                            {validationErrors.sowStatusError}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          PO Status <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        {!isEdit ? (
                          <TextField
                            id="poStatus"
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'PO Status' && !field.edit
                              )
                            }
                            inputProps={{ style: { fontSize: '13px' } }}
                            size="small"
                            placeholder="Project Type"
                            variant="outlined"
                            value={projectDetails.poStatus}
                          />
                        ) : (
                          <Select
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'PO Status' && !field.edit
                              )
                            }
                            className="select-text"
                            size="small"
                            displayEmpty
                            inputProps={{ style: { fontSize: '13px' } }}
                            value={projectDetails.poStatus}
                            onChange={(e) => {
                              setProjectDetails((prev) => ({
                                ...prev,
                                poStatus: e.target.value
                              }));
                              setValidationErrors((prev) => ({
                                ...prev,
                                poStatusError: ''
                              }));
                            }}
                          >
                            <MenuItem value="Received">Received</MenuItem>
                            <MenuItem value="Not Received">Not Received</MenuItem>
                          </Select>
                        )}
                        {validationErrors.poStatusError && (
                          <Typography
                            style={{
                              paddingLeft: 15,
                              paddingRight: 15,
                              fontSize: '11px',
                              textTransform: 'capitalize'
                            }}
                            color="red"
                          >
                            {validationErrors.poStatusError}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Note for Invoice
                        </Typography>
                        <TextField
                          id="noteForInvoice"
                          disabled={
                            !isEdit ||
                            fields.some(
                              (field) =>
                                field.name === 'Note For Invoice' && !field.edit
                            )
                          }
                          inputProps={{ style: { fontSize: '13px' } }}
                          size="small"
                          className="input-text"
                          fullWidth
                          placeholder="Enter Note for Invoice"
                          variant="outlined"
                          multiline
                          rows={3}
                          value={projectDetails.noteForInvoice}
                          onChange={(e) => {
                            if (e.target.value.length <= 100) {
                              setProjectDetails((prev) => ({
                                ...prev,
                                noteForInvoice: e.target.value
                              }));
                            }
                          }}
                        />
                        {validationErrors.invoiceNoteError && (
                          <Typography
                            style={{
                              paddingLeft: 15,
                              paddingRight: 15,
                              fontSize: '11px',
                              textTransform: 'capitalize'
                            }}
                            color="red"
                          >
                            {validationErrors.invoiceNoteError}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Typography
                    variant="h4"
                    sx={{ pt: 4, pb: 2 }}
                    style={{ fontSize: '14px' }}
                  >
                    Time Estimation
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Typography
                          style={{ fontSize: '13px' }}
                          color="GrayText"
                        >
                          Planned Hours <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <TextField
                          id="plannedHours"
                          disabled={
                            !isEdit ||
                            fields.some(
                              (field) =>
                                field.name === 'Planned Hours' && !field.edit
                            )
                          }
                          inputProps={{ style: { fontSize: '13px' } }}
                          size="small"
                          className="input-text"
                          value={projectDetails.plannedHours}
                          onChange={(event) => {
                            if (
                              (isDecimal(event.target.value) &&
                                event.target.value.length <= 5) ||
                              event.target.value == ''
                            ) {
                              setProjectDetails((prev) => ({
                                ...prev,
                                plannedHours: event.target.value
                              }));
                              setValidationErrors((prev) => ({
                                ...prev,
                                plannedHoursError: ''
                              }));
                            }
                          }}
                          fullWidth
                          placeholder="Enter Planned Hours"
                          variant="outlined"
                          type="text"
                        />
                        {validationErrors.plannedHoursError && (
                          <Typography
                            style={{
                              paddingLeft: 15,
                              paddingRight: 15,
                              fontSize: '11px',
                              textTransform: 'capitalize'
                            }}
                            color="red"
                          >
                            {validationErrors.plannedHoursError}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    {projectDetails.type === 'Delivery' && (
                      <Grid item xs={6} sx={{ pt: 2 }}>
                        <Typography
                          className="lable-text"
                          style={{ fontSize: '13px' }}
                          fontWeight="light"
                          variant="h5"
                        >
                          Revised Planned Hours
                        </Typography>
                        <TextField
                          id="revisedPlannedHours"
                          size="small"
                          disabled={
                            !isEdit ||
                            fields.some(
                              (field) =>
                                field.name === 'Revised Planned Hours' &&
                                !field.edit
                            )
                          }
                          className="input-text"
                          value={projectDetails.revisedPlannedHours}
                          onChange={(event) => {
                            if (
                              (isDecimal(event.target.value) &&
                                event.target.value.length <= 5) ||
                              event.target.value == ''
                            ) {
                              setProjectDetails((prev) => ({
                                ...prev,
                                revisedPlannedHours: event.target.value
                              }));
                              setValidationErrors((prev) => ({
                                ...prev,
                                revisedPlannedHoursError: ''
                              }));
                            }
                          }}
                          fullWidth
                          placeholder="Revised Planned Hours"
                          variant="outlined"
                          type="text"
                          inputProps={{ style: { fontSize: '13px' } }}
                        />
                        {validationErrors.revisedPlannedHoursError && (
                          <Typography
                            style={{
                              paddingLeft: 15,
                              paddingRight: 15,
                              fontSize: '11px'
                            }}
                            color="red"
                          >
                            {validationErrors.revisedPlannedHoursError}&nbsp;
                          </Typography>
                        )}
                      </Grid>
                    )}
                    {projectDetails.type === 'Pre-sale' && (
                      <Grid item xs={6}>
                        <FormControl
                          className="project-edit-datepicker"
                          fullWidth
                        >
                          <Typography
                            style={{ fontSize: '13px' }}
                            color="GrayText"
                          >
                            Pre-sale Start Date{' '}
                            <span style={{ color: 'red' }}>*</span>
                          </Typography>
                          <DatePicker
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Pre-Sale Start Date' &&
                                  !field.edit
                              )
                            }
                            InputProps={{
                              className: 'date-picker-project'
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                id="preSalesStartDate"
                              />
                            )}
                            value={
                              projectDetails?.preSalesStartDate?.split('T')[0]
                                ? projectDetails?.preSalesStartDate?.split('T')[0]
                                : ''
                            }
                            onChange={(newValue) => {
                              if (newValue) {
                                setProjectDetails((prev) => ({
                                  ...prev,
                                  preSalesStartDate: moment(newValue).format('YYYY-MM-DD')
                                }));
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  preSalesStartDateError: ''
                                }));
                              }
                            }}
                          />
                          {validationErrors.preSalesStartDateError && (
                            <Typography
                              style={{
                                paddingLeft: 15,
                                paddingRight: 15,
                                fontSize: '11px',
                                textTransform: 'capitalize'
                              }}
                              color="red"
                            >
                              {validationErrors.preSalesStartDateError}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                    {projectDetails.type === 'Pre-sale' && (
                      <Grid item xs={6}>
                        <FormControl
                          className="project-edit-datepicker"
                          fullWidth
                        >
                          <Typography
                            style={{ fontSize: '13px' }}
                            color="GrayText"
                          >
                            Pre-sale End Date{' '}
                            <span style={{ color: 'red' }}>*</span>
                          </Typography>
                          <DatePicker
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Pre-Sale End Date' &&
                                  !field.edit
                              )
                            }
                            InputProps={{
                              className: 'date-picker'
                            }}
                            minDate={new Date(projectDetails.preSalesStartDate)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                id="preSalesEndDate"
                              />
                            )}
                            value={
                              projectDetails?.preSalesEndDate?.split('T')[0]
                                ? projectDetails?.preSalesEndDate?.split('T')[0]
                                : ''
                            }
                            onChange={(newValue) => {
                              if (newValue) {
                                setProjectDetails((prev) => ({
                                  ...prev,
                                  preSalesEndDate: moment(newValue).format('YYYY-MM-DD')
                                }));
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  preSalesEndDateError: ''
                                }));
                              }
                            }}
                          />
                          {validationErrors.preSalesEndDateError && (
                            <Typography
                              style={{
                                paddingLeft: 15,
                                paddingRight: 15,
                                fontSize: '11px',
                                textTransform: 'capitalize'
                              }}
                              color="red"
                            >
                              {validationErrors.preSalesEndDateError}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                    {projectDetails.type === 'Delivery' && (
                      <Grid item xs={6}>
                        <FormControl
                          className="project-edit-datepicker"
                          fullWidth
                        >
                          <Typography
                            style={{ fontSize: '13px' }}
                            color="GrayText"
                          >
                            Actual Start Date
                          </Typography>
                          <DatePicker
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Actual Start Date' &&
                                  !field.edit
                              )
                            }
                            InputProps={{
                              className: 'date-picker'
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                id="actualStartDate"
                              />
                            )}
                            value={
                              projectDetails?.actualStartDate?.split('T')[0] ==
                              '0001-01-01'
                                ? ''
                                : projectDetails?.actualStartDate?.split('T')[0] ? projectDetails?.actualStartDate?.split('T')[0] : ''
                            }
                            onChange={(newValue) => {
                              if (newValue) {
                                setProjectDetails((prev) => ({
                                  ...prev,
                                  actualStartDate: moment(newValue).format('YYYY-MM-DD')
                                }));
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  actualStartDateError: ''
                                }));
                              }
                            }}
                          />
                          {validationErrors.actualStartDateError && (
                            <Typography
                              style={{
                                paddingLeft: 15,
                                paddingRight: 15,
                                fontSize: '11px',
                                textTransform: 'capitalize'
                              }}
                              color="red"
                            >
                              {validationErrors.actualStartDateError}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                    {projectDetails.type === 'Delivery' && (
                      <Grid item xs={6}>
                        <FormControl
                          className="project-edit-datepicker"
                          fullWidth
                        >
                          <Typography
                            style={{ fontSize: '13px' }}
                            color="GrayText"
                          >
                            Planned Start Date{' '}
                            <span style={{ color: 'red' }}>*</span>
                          </Typography>
                          <DatePicker
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Planned Start Date' &&
                                  !field.edit
                              )
                            }
                            InputProps={{
                              className: 'date-picker'
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                id="plannedStartDate"
                              />
                            )}
                            value={
                              projectDetails?.plannedStartDate?.split('T')[0] ==
                              '0001-01-01'
                                ? ''
                                : projectDetails?.plannedStartDate?.split(
                                    'T'
                                  )[0] ? projectDetails?.plannedStartDate?.split('T')[0] : ''
                            }
                            onChange={(newValue) => {
                              if (newValue) {
                                setProjectDetails((prev) => ({
                                  ...prev,
                                  plannedStartDate: moment(newValue).format('YYYY-MM-DD')
                                }));
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  plannedStartDateError: ''
                                }));
                              }
                            }}
                          />
                          {validationErrors.plannedStartDateError && (
                            <Typography
                              style={{
                                paddingLeft: 15,
                                paddingRight: 15,
                                fontSize: '11px',
                                textTransform: 'capitalize'
                              }}
                              color="red"
                            >
                              {validationErrors.plannedStartDateError}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                    {projectDetails.type === 'Delivery' && (
                      <Grid item xs={6}>
                        <FormControl
                          className="project-edit-datepicker"
                          fullWidth
                        >
                          <Typography
                            style={{ fontSize: '13px' }}
                            color="GrayText"
                          >
                            Planned End Date{' '}
                            <span style={{ color: 'red' }}>*</span>
                          </Typography>
                          <DatePicker
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Planned End Date' &&
                                  !field.edit
                              )
                            }
                            InputProps={{
                              className: 'date-picker'
                            }}
                            minDate={new Date(projectDetails.plannedStartDate)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                id="plannedEndDate"
                              />
                            )}
                            value={
                              projectDetails?.plannedEndDate?.split('T')[0] ==
                              '0001-01-01'
                                ? ''
                                : projectDetails?.plannedEndDate?.split('T')[0] ? projectDetails?.plannedEndDate?.split('T')[0] : '' 
                            }
                            onChange={(newValue) => {
                              if (newValue) {
                                setProjectDetails((prev) => ({
                                  ...prev,
                                  plannedEndDate: moment(newValue).format('YYYY-MM-DD')
                                }));
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  plannedEndDateError: ''
                                }));
                              }
                            }}
                          />
                          {validationErrors.plannedEndDateError && (
                            <Typography
                              style={{
                                paddingLeft: 15,
                                paddingRight: 15,
                                fontSize: '11px',
                                textTransform: 'capitalize'
                              }}
                              color="red"
                            >
                              {validationErrors.plannedEndDateError}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                    {projectDetails.type === 'Delivery' && (
                      <Grid item xs={6}>
                        <FormControl
                          className="project-edit-datepicker"
                          fullWidth
                        >
                          <Typography
                            style={{ fontSize: '13px' }}
                            color="GrayText"
                          >
                            Actual End Date
                          </Typography>
                          <DatePicker
                            disabled={
                              !isEdit ||
                              fields.some(
                                (field) =>
                                  field.name === 'Actual End Date' &&
                                  !field.edit
                              )
                            }
                            InputProps={{
                              className: 'date-picker'
                            }}
                            minDate={new Date(projectDetails.actualStartDate)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                id="actualStartDate"
                              />
                            )}
                            value={
                              projectDetails?.actualEndDate?.split('T')[0] ==
                              '0001-01-01'
                                ? ''
                                : projectDetails?.actualEndDate?.split('T')[0] ? projectDetails?.actualEndDate?.split('T')[0] : ''
                            }
                            onChange={(newValue) => {
                              if (newValue) {
                                setProjectDetails((prev) => ({
                                  ...prev,
                                  actualEndDate: moment(newValue).format('YYYY-MM-DD')
                                }));
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  actualEndDateError: ''
                                }));
                              }
                            }}
                          />
                          {validationErrors.actualEndDateError && (
                            <Typography
                              style={{
                                paddingLeft: 15,
                                paddingRight: 15,
                                fontSize: '11px',
                                textTransform: 'capitalize'
                              }}
                              color="red"
                            >
                              {validationErrors.actualEndDateError}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                    {permissions.some(
                      (perm) =>
                        perm.name === 'End Project' && perm.value === true
                    ) && (
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <FormControlLabel
                            disabled={!isEdit}
                            style={{ marginTop: 10 }}
                            control={
                              <Checkbox
                                id="checkbox-isClosed"
                                checked={isClosed}
                                onChange={(e) => {
                                  setIsClosed(e.target.checked);
                                }}
                              />
                            }
                            label="End Project"
                          />
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                  {isEdit && (
                    <Grid
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-end'
                      }}
                    >
                      <Button
                        id="btn-project-details"
                        type="button"
                        onClick={() => navigate('/project-details')}
                        style={{ fontSize: '12px', fontWeight: 'normal' }}
                        sx={{ marginRight: 2, mb: 3, mt: 2 }}
                        variant="outlined"
                      >
                        Cancel
                      </Button>
                      <Button
                        id="btn-toggleConfirm"
                        type="button"
                        style={{ fontSize: '12px', fontWeight: 'normal' }}
                        sx={{ marginRight: 2, mb: 3, mt: 2 }}
                        variant="contained"
                        onClick={() => toggleConfirm()}
                      >
                        Update
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
        <ConfirmationModal
          isLoading={isLoading}
          open={openConfirmationModal}
          toggle={toggleConfirm}
          message={`Are you sure you want to update this project details?`}
          onConfirm={() => onSubmit()}
          noToggle={toggleConfirm}
        />
        <SnackBar
          snackBarOpen={snackBarOpen}
          setSnackBarOpen={setSnackBarOpen}
          snackBarMessage={snackBarMessage}
          snackbarSeverity={snackbarSeverity}
          isClickContinue={handleChildPropData}
        />
      </Container>
    </>
  );
};

export default ProjectEditForm;
